import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import NeoButton from "./NeoButton";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import Toast from "./Toast";
import {
  isEventRoomForLobbyList,
  subscribeToOpenLobbyRooms,
} from "../@shared/utils/roomFunctions";
import { joinRoomRTDBWithValidation } from "../@shared/utils/joinRoomHelpers";
import ModalComponent from "./ModalComponent";
import { getBadgeByLevel } from "../@shared/utils/Utils";
import { getRandomItem } from "../@shared/game/botActions";
import {
  GAME_MODES,
  getModeFromRoom,
  MODE_CONFIGS,
} from "../@shared/game/modes";
import { HeaderActionButton } from "./ui";

const screenHeight = window.innerHeight;

const modalHeight = screenHeight * 0.6;
const modalWidth = modalHeight * (4 / 5);

const ethIcon = require("../@shared/assets/icons/eth.png");
const tokenIcon = require("../@shared/assets/infift_token.png");


const JoinRoomModal = ({ visible, onClose, userId, setStartRoomVisible }) => {
  const { userData } = useAuthStore();
  const [codeInput, setCodeInput] = useState("");
  //   const fetchLobbyRooms = useRoomsStore((s) => s.fetchLobbyRooms);
  const joinAndSetRoom = useRoomsStore((s) => s.joinAndSetRoom);
  //   const clearRooms = useRoomsStore((s) => s.clearRooms);
  const navigate = useNavigate();
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joiningSource, setJoiningSource] = useState(null);
  const [joiningRoomCode, setJoiningRoomCode] = useState(null);
  const [rooms, setRooms] = useState([]);
  const { Theme, setScreenLoading, deviceType } = useContext(MainContext);
  const { setMessage, setError, setInfo, setSuccess } = useContext(MainContext);
  const joinInFlightRef = useRef(false);

  useEffect(() => {
    if (!visible) return undefined;

    setRoomsLoading(true);
    let settled = false;
    const loadingFailSafe = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setRooms([]);
      setRoomsLoading(false);
    }, 5000);

    const unsubscribe = subscribeToOpenLobbyRooms(
      (nextRooms) => {
        settled = true;
        window.clearTimeout(loadingFailSafe);
        setRooms(nextRooms);
        setRoomsLoading(false);
      },
      () => {
        settled = true;
        window.clearTimeout(loadingFailSafe);
        setRooms([]);
        setRoomsLoading(false);
      },
    );

    return () => {
      window.clearTimeout(loadingFailSafe);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [visible]);

  const publicRooms = rooms.filter(
    (room) => room?.public === true && !isEventRoomForLobbyList(room),
  );

  useEffect(() => {
    if (visible) return;
    setCodeInput("");
    setRooms([]);
    setRoomsLoading(false);
    setJoining(false);
    setJoiningSource(null);
    setJoiningRoomCode(null);
    joinInFlightRef.current = false;
  }, [visible]);

  const handleJoinRoom = async (roomId = null, source = "manual") => {
    const targetAccessCode = String(roomId ?? codeInput.trim()).trim();
    if (!targetAccessCode || joinInFlightRef.current) return;

    joinInFlightRef.current = true;
    setJoining(true);
    setJoiningSource(source);
    setJoiningRoomCode(targetAccessCode);

    try {
      const { roomData } = await joinRoomRTDBWithValidation({
        userData,
        joinAndSetRoom,
        accessCode: targetAccessCode,
      });

      setCodeInput("");
      onClose();
      setScreenLoading(true);

      setTimeout(() => {
        navigate(`/lobby/${roomData?.accessCode}`);
      }, 300);
    } catch (err) {
      console.error("? Error joining room:", err);
      setMessage(err?.message || "Failed to join room.");
      setError(true);
    } finally {
      joinInFlightRef.current = false;
      setJoining(false);
      setJoiningSource(null);
      setJoiningRoomCode(null);
    }
  };
  const handleQuickMatch = () => {
    if (joinInFlightRef.current) return;

    const availRooms = publicRooms?.filter((r) => {
      const playerCount =
        Number(r?.playerCount) ||
        (Array.isArray(r?.playerIds) ? r.playerIds.length : 0);
      return playerCount < Number(r?.amountOfPlayers || 0);
    });
    console.log("Here are the avail rooms", availRooms);
    const randomRoom = getRandomItem(availRooms);
    if (randomRoom) {
      handleJoinRoom(randomRoom?.accessCode, "quick");
    }
  };

  const handleNoRooms = () => {
    setMessage("There are no rooms to join at this time.");
    setError(true);
  };

  const handleClose = () => {
    if (joining) return;
    setRooms([]);
    onClose();
  };

  return (
    <ModalComponent
      aspectRatio={deviceType === "desktop" ? "6/7" : "5/8"}
      ingnorAspect={false}
      md={5}
      lg={4.5}
      // maxHeight={deviceType === "desktop" && 75}
      modalOpen={visible}
      setModalOpen={joining ? () => {} : handleClose}
    >
      <Toast onFinish={() => { }} timeTilClose={3000} />

      <Typography
        variant="h6"
        sx={{
          color: "#fff",
          fontWeight: "bold",
          // mb: 2,
          textAlign: "center",
          padding: "10px",
          borderRadius: "5px",
          backgroundColor: "#00000080",
          width: "100%",
          height: "fit-content",
        }}
      >
        Room List
      </Typography>

      <Box sx={{ ...styles.codeRow, width: "100%", height: "fit-content" }}>
        <TextField
          variant="outlined"
          size="small"
          type="number"
          placeholder="Enter 7-digit Room Code"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value)}
          disabled={joining}
          sx={{
            flex: 1,
            width: "70%",
          }}
        />
        <HeaderActionButton
          label={joiningSource === "manual" ? "Joining..." : "Join"}
          tone="gold"
          onClick={() => handleJoinRoom(null, "manual")}
          disabled={joining || !codeInput.trim()}
        />
      </Box>

      <Typography
        sx={{
          color: "#fff",
          mt: 1,
          mb: 1,
          width: "100%",
          height: "fit-content",
        }}
      >
        Public Rooms
      </Typography>

      {roomsLoading ? (
        <Box sx={{ py: 2, textAlign: "center" }}>
          <CircularProgress size={30} sx={{ color: "#fff" }} />
          <Typography sx={{ color: "#fff", mt: 2 }}>
            Loading rooms...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ ...styles.scrollArea, width: "100%" }}>
          {publicRooms.length === 0 ? (
            <Typography
              sx={{
                color: "#fff",
                // height: 240,
                width: "100%",
                textAlign: "center",
              }}
            >
              No rooms available
            </Typography>
          ) : (
            publicRooms.map((room, i) => {
              const playersArray = Object.values(room.players || {});
              const roomOwner =
                room?.roomOwnerProfile ||
                playersArray.find((p) => p.id === room.roomOwner) ||
                null;
              const playerCount =
                Number(room?.playerCount) ||
                (Array.isArray(room?.playerIds)
                  ? room.playerIds.length
                  : playersArray.length);
              const isRoomFull = playerCount >= Number(room?.amountOfPlayers || 0);
              const wagerCurrency = room?.wager?.currency || "tokens";
              const wagerLabel = wagerCurrency === "eth" ? "ETH" : "Infinity Tokens";
              const wagerImg = wagerCurrency === "eth" ? ethIcon : tokenIcon;
              const roomOwnerName = roomOwner?.username || "Unknown";
              const roomHostBadge = getBadgeByLevel(roomOwner?.level)?.badge;
              const roomNumber = String(room?.accessCode || room?.id || "Unknown");

              const roomMode = getModeFromRoom(room);
              const modeConfig =
                MODE_CONFIGS[roomMode] || MODE_CONFIGS[GAME_MODES.ROYALE];

              const iconSrc = {
                swords: require("../@shared/assets/icons/swords_icon2.png"),
                shield_star: require("../@shared/assets/icons/sheild_star_icon.png"),
                both: require("../@shared/assets/icons/nfteams.png"),
              }[modeConfig.iconKey];

              const label = modeConfig.label;

              return (
                <Box
                  key={room.id}
                  sx={{
                    ...styles.roomRow,
                    marginBottom: i === publicRooms.length - 1 ? 8 : 1,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 1,
                      background:
                        "linear-gradient(90deg, rgba(11,7,34,0.95) 0%, rgba(72,34,140,0.85) 100%)",
                      borderRadius: "10px",
                      px: 1.2,
                      py: 0.55,
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.72)",
                          fontSize: { xs: 9, md: 10 },
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        Room Number
                      </Typography>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: { xs: 14, md: 16 },
                          lineHeight: 1.1,
                        }}
                      >
                        #{roomNumber}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        ml: "auto",
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "999px",
                        backgroundColor: isRoomFull
                          ? "rgba(255,255,255,0.14)"
                          : "rgba(119,255,189,0.16)",
                        border: isRoomFull
                          ? "1px solid rgba(255,255,255,0.16)"
                          : "1px solid rgba(119,255,189,0.35)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: isRoomFull ? "#fff" : "#9DFFCC",
                          fontWeight: "bold",
                          fontSize: { xs: 10, md: 11 },
                        }}
                      >
                        {isRoomFull ? "Full" : "Open"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", md: "44%" }, containerType: "inline-size" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component={"img"}
                        src={roomHostBadge}
                        sx={{ width: { xs: "32%", md: "10%" }, height: "auto" }}
                        alt=""
                      />
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          ml: { xs: 0, md: "5px" },
                          fontSize: { xs: "10cqw", md: "7.5cqw" },
                        }}
                      >
                        {roomOwnerName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component={"img"}
                        src={require("../@shared/assets/icons/avatar_icon.png")}
                        sx={{ width: { xs: "32%", md: "10%" }, height: "auto" }}
                        alt=""
                      />
                      <Typography
                        sx={{
                          color: "#fff",
                          ml: { xs: 0, md: "5px" },
                          fontSize: { xs: "10cqw", md: "7.5cqw" },
                        }}
                      >
                        {playerCount === room.amountOfPlayers
                          ? "FULL"
                          : `${playerCount + "/" + room.amountOfPlayers
                          } players`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component={"img"}
                        src={require("../@shared/assets/icons/target.png")}
                        sx={{ width: { xs: "32%", md: "10%" }, height: "auto" }}
                        alt=""
                      />
                      <Typography
                        sx={{
                          color: "#fff",
                          ml: { xs: 0, md: "5px" },
                          fontSize: { xs: "10cqw", md: "7.5cqw" },
                        }}
                      >
                        Bots: {room.botFill ? "On" : "Off"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: { xs: "100%", md: "30%" }, containerType: "inline-size" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component={"img"}
                        src={
                          room.ranked
                            ? require("../@shared/assets/icons/clans_icon.png")
                            : require("../@shared/assets/icons/flag_icon.png")
                        }
                        sx={{ width: { xs: "32%", md: "14%" }, height: "auto" }}
                        alt=""
                      />
                      <Box
                        component={"img"}
                        src={iconSrc}
                        sx={{ width: { xs: "32%", md: "14%" }, height: "auto" }}
                        alt=""
                      />
                      <Typography
                        sx={{
                          color: "#fff",
                          ml: "5px",
                          fontSize: { xs: "15cqw", md: "8.5cqw" },
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          ml: { xs: 0, md: "5px" },
                          fontSize: { xs: "15cqw", md: "8.5cqw" },
                        }}
                      >
                        WAGER:
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component={"img"}
                        src={wagerImg}
                        sx={{ width: { xs: "32%", md: "14%" }, height: "auto" }}
                        alt=""
                      />
                      <Typography
                        sx={{
                          color: "#fff",
                          ml: "5px",
                          fontSize: { xs: "15cqw", md: "8.5cqw" },
                        }}
                      >
                        {room?.wager?.amount} {window.innerWidth < 400 ? "" : wagerLabel}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: "100%", md: "30%" },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <NeoButton
                      text={
                        joiningSource === "list" &&
                          joiningRoomCode === String(room.accessCode)
                          ? "Joining..."
                          : "Join"
                      }
                      width={window.innerWidth < 400 ? 100 : 90}
                      height={35}
                      color={Theme.palette.primary.main}
                      onClick={() => handleJoinRoom(room.accessCode, "list")}
                      disabled={joining || isRoomFull}
                    />
                  </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      )}
      {!roomsLoading && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
          }}
        >
          <NeoButton
            text={joiningSource === "quick" ? "Joining..." : "Quick Match"}
            color={
              publicRooms.length === 0
                ? Theme.palette.primary.light
                : Theme.palette.primary.main
            }
            width={window.innerWidth < 400 ? 45 : 40}
            textSize={window.innerWidth < 400 && 20}
            onClick={() => {
              publicRooms.length === 0 ? handleNoRooms() : handleQuickMatch();
            }}
            disabled={joining || publicRooms.length === 0}
          />
          <NeoButton
            text="Create Room"
            color={Theme.palette.tirciary.main}
            width={window.innerWidth < 400 ? 45 : 40}
            textSize={window.innerWidth < 400 && 20}
            onClick={() => {
              setStartRoomVisible(true);
            }}
            disabled={joining}
          />
        </Box>
      )}
    </ModalComponent>
  );
};

const styles = {
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: { xs: "70%", md: "60%" },
    width: { xs: "95%", md: "30%" },
    position: "absolute",
    outline: "none",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  modalContentWrapper: {
    width: modalWidth,
    height: modalHeight,
  },
  modalContent: {
    width: modalWidth,
    height: modalHeight,
    paddingTop: "5.6%",
    paddingLeft: "8%",
    paddingRight: "8%",
    paddingBottom: "12%",
    borderRadius: 16,
    elevation: 5,
    borderColor: "#fff",
    position: "relative",
  },
  codeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    // mb: 2,
  },
  scrollArea: {
    mt: 0.5,
    maxHeight: "45%",
    overflowY: "auto",
    scrollbarWidth: "none",
  },
  roomRow: {
    background:
      "linear-gradient(180deg, rgba(98,51,173,0.95) 0%, rgba(57,25,109,0.96) 100%)",
    p: 1.2,
    borderRadius: "12px",
    mb: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1,
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
  },
  buttonRow: {
    mt: 2,
  },
};

export default JoinRoomModal;
